import { Customer } from '../../contacts/models/customer'
import { EndUser } from '../../contacts/models/end-user'
import { User } from '../../core/models/user'
import { Version } from '../../core/models/version'
import { VersionSummary } from '../../core/models/version-summary'
import { Category } from '../../organisation/models/category'
import { Mappable, Mapped } from '../../rest/decorators/mapped'
import { RestQuery } from '../../rest/utilities/rest-query'
import { HardwareRegistration } from '../../stream/models/hardware-registration'
import { StreamCommission } from '../../stream/models/stream-commission'
import { JobAccountingStatus } from './job-accounting-status'
import { JobPartsAndLabour } from './job-parts-and-labour'
import { JobReference } from './job-reference'
import { JobSignature } from './job-signature'
import { JobSignatureNomination } from './job-signature-nomination'
import { JobType } from './job-type'

@Mapped(
  {
    first_name: { property: 'firstName' },
    last_name: { property: 'lastName' },
  },
  ['numericID']
)
class AllocatedEngineer extends Mappable {
  id!: string | number
  numericID!: number
  firstName!: string
  lastName!: string

  static create<Data>(attrs: Data) {
    return new AllocatedEngineer().map(attrs)
  }
}

@Mapped({
  date: { type: 'date' },
  start_at: { property: 'start', type: 'date' },
  end_at: { property: 'end', type: 'date' },
  engineer: { format: AllocatedEngineer.create },
  registration: { property: 'vehicleRegistration' },
  chassis_number: { property: 'chassisNumber' },
})
class Allocation extends Mappable {
  id!: string | number
  date!: Date
  start!: Date
  end!: Date
  engineer?: AllocatedEngineer
  vehicleRegistration!: string | null
  chassisNumber!: string | null

  static create<Data>(attrs: Data) {
    return new Allocation().map(attrs)
  }
}

interface JobVehicle {
  registration: string
  chassis: string
  summary: string
}

@Mapped(
  {
    internal_id: { property: 'internalID' },
    types: { format: JobType.create },
    customer: { format: Customer.create },
    end_user: { property: 'endUser', format: EndUser.create },
    endUser: { format: EndUser.create },
    category: { format: Category.create },
    admin_notes: { property: 'adminNotes' },
    invoice_notes: { property: 'invoiceNotes' },
    timesheet_hours: { property: 'timesheetHours' },
    price_code: { property: 'priceCode' },
    total_value: { property: 'totalValue' },
    invoice_number: { property: 'invoiceNumber' },
    job_number: { property: 'jobNumber' },
    estimated_days: { property: 'estimatedDays' },
    estimated_hours: { property: 'estimatedHours' },
    parts_comments: { property: 'partsComments' },
    parts_and_labour: {
      property: 'partsAndLabour',
      format: JobPartsAndLabour.create,
    },
    partsAndLabour: { format: JobPartsAndLabour.create },
    work_required: { property: 'workRequired' },
    sales_person: { property: 'salesPerson' },
    internal_comments: { property: 'internalComments' },
    start_on: { property: 'start', type: 'date' },
    sent_to_stores_at: { property: 'sentToStores', type: 'date' },
    sent_to_stores_by: { property: 'sentToStoresBy', format: User.create },
    sentToStoresBy: { format: User.create },
    to_recall_at: { property: 'toRecall', type: 'date' },
    recalled_at: { property: 'recalled', type: 'date' },
    completed_at: { property: 'complete', type: 'date' },
    accounting_status: {
      property: 'accountingStatus',
      format: JobAccountingStatus.create,
    },
    accountingStatus: { format: JobAccountingStatus.create },
    ready_to_invoice_at: { property: 'readyToInvoice', type: 'date' },
    invoiced_at: { property: 'invoiced', type: 'date' },
    free_of_charge_at: { property: 'freeOfCharge', type: 'date' },
    invoice_excepted_at: { property: 'invoiceExcepted', type: 'date' },
    nominations: { format: JobSignatureNomination.create },
    signatures: { format: JobSignature.create },
    first_allocation: {
      property: 'firstAllocation',
      format: Allocation.create,
    },
    firstAllocation: { format: Allocation.create },
    last_allocation: {
      property: 'lastAllocation',
      format: Allocation.create,
    },
    lastAllocation: { format: Allocation.create },
    allocations: { format: Allocation.create },
    is_welfare: { property: 'isWelfare' },
    welfare_payment_method: { property: 'welfarePaymentMethod' },
    welfare_registered_to: { property: 'welfareRegisteredTo' },
    welfare_address: { property: 'welfareAddress' },
    welfare_deliver_on: { property: 'welfareDeliveryDate', type: 'date' },
    welfare_customer_name: { property: 'welfareCustomerName' },
    welfare_customer_email: { property: 'welfareCustomerEmail' },
    welfare_customer_phone: { property: 'welfareCustomerPhone' },
    welfare_van_number: { property: 'welfareVanNumber' },
    welfare_registration: { property: 'welfareRegistration' },
    welfare_registered_on: {
      property: 'welfareRegistrationDate',
      type: 'date',
    },
    welfare_chassis: { property: 'welfareChassis' },
    welfare_order_number: { property: 'welfareOrderNumber' },
    welfare_extras_invoice: { property: 'welfareExtrasInvoice' },
    welfare_quote_number: { property: 'welfareQuoteNumber' },
    welfare_build_job: {
      property: 'welfareBuildJob',
      format: JobReference.create,
    },
    welfareBuildJob: { format: JobReference.create },
    welfare_extras_job: {
      property: 'welfareExtrasJob',
      format: JobReference.create,
    },
    welfareExtrasJob: { format: JobReference.create },
    stream_commission: {
      property: 'streamCommission',
      format: StreamCommission.create,
    },
    streamCommission: { format: StreamCommission.create },
    hardware_registration: {
      property: 'hardwareRegistration',
      format: HardwareRegistration.create,
    },
    hardwareRegistration: { format: HardwareRegistration.create },
    versions: { format: VersionSummary.create },
    current_version: { property: 'currentVersion', format: Version.create },
  },
  ['numericID', 'timestamps']
)
export class Job extends Mappable {
  id!: number
  numericID!: number
  ulid!: string
  internalID!: string
  types?: JobType[]
  customer?: Customer
  endUser?: EndUser
  category?: Category
  adminNotes!: string
  invoiceNotes!: string
  timesheetHours!: number
  priceCode!: string
  totalValue!: number | null
  invoiceNumber!: string
  jobNumber!: string
  address!: string | null
  postcode!: string | null
  latitude!: number | null
  longitude!: number | null
  estimatedDays!: number | null
  estimatedHours!: number | null
  partsComments!: string
  partsAndLabour?: JobPartsAndLabour
  comments!: string
  workRequired!: string
  salesPerson!: string
  internalComments!: string
  start!: Date | null
  sentToStores!: Date | null
  sentToStoresBy?: User
  toRecall!: Date | null
  recalled!: Date | null
  complete!: Date | null
  accountingStatus?: JobAccountingStatus
  readyToInvoice!: Date | null
  invoiced!: Date | null
  freeOfCharge!: Date | null
  invoiceExcepted!: Date | null

  nominations?: JobSignatureNomination[]
  signatures?: JobSignature[]
  firstAllocation?: Allocation
  lastAllocation?: Allocation
  allocations?: Allocation[]

  isWelfare!: boolean
  welfarePaymentMethod!: string
  welfareRegisteredTo!: string
  welfareAddress!: string
  welfareDeliveryDate!: Date | null
  welfareCustomerName!: string
  welfareCustomerEmail!: string
  welfareCustomerPhone!: string
  welfareVanNumber!: string
  welfareRegistration!: string
  welfareRegistrationDate!: Date | null
  welfareChassis!: string
  welfareOrderNumber!: string
  welfareExtrasInvoice!: string
  welfareQuoteNumber!: string
  welfareBuildJob?: JobReference
  welfareExtrasJob?: JobReference

  streamCommission?: StreamCommission
  hardwareRegistration?: HardwareRegistration

  versions?: VersionSummary
  currentVersion?: Version
  deleted!: boolean

  hasType(typeID: string) {
    return (this.types ?? []).map((type) => type.id).includes(typeID)
  }

  get vehicles(): JobVehicle[] {
    const vehicles: Record<string, JobVehicle> = {}
    if (!this.allocations?.length) {
      return []
    }
    for (const allocation of this.allocations) {
      const registration =
        allocation.vehicleRegistration?.trim().toUpperCase() ?? ''
      const chassis = allocation.chassisNumber?.trim().toUpperCase() ?? ''
      if (registration === '' && chassis === '') {
        continue
      }
      const key = [registration, chassis].join('-')
      if (!Object.prototype.hasOwnProperty.call(vehicles, key)) {
        vehicles[key] = {
          registration,
          chassis,
          summary: [registration, chassis]
            .filter((p) => p.length > 0)
            .join(' / '),
        }
      }
    }
    return Object.values(vehicles)
  }

  static create<Data>(attrs: Data) {
    return new Job().map(attrs)
  }
}

interface Filters {
  keywords?: string
  search_by_id?: string
  exclude?: string
  before?: string
  after?: string
  parts_required?: boolean
  recalling?: boolean
  archive?: string
  accounting_status?: string
  last_allocation_in_range?: {
    start: string
    end: string
  }
  external_customers_only?: boolean
  same_project?: string | number
}
type Ordering =
  | 'id'
  | '-id'
  | 'first_allocation'
  | '-first_allocation'
  | 'customer'
  | '-customer'
  | 'end_user'
  | '-end_user'
  | 'postcode'
  | '-postcode'
  | 'last_allocation'
  | '-last_allocation'
type Includes =
  | 'firstAllocation'
  | 'lastAllocation'
  | 'customer'
  | 'endUser'
  | 'allocations'
  | 'allocations.engineer'
  | 'allocations.updates'
  | 'stockGroups'
  | 'stockGroups.products'
  | 'stockGroups.products.product'
  | 'stockGroups.products.stockItems'
  | 'stockGroups.products.stockItems.location'
  | 'partsAndLabour'
  | 'category'
  | 'signatures'
  | 'signatures.file'
  | 'nominations'
  | 'streamCommission'
  | 'streamCommission.images'
  | 'streamCommission.images.file'
  | 'hardwareRegistration'
  | 'hardwareRegistration.createdBy'
  | 'hardwareRegistration.registeredBy'

export class JobQuery extends RestQuery<Filters, Ordering, Includes> {}
